import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { FormattedMessage, FormattedDuration } from 'LearningCenterUI/components/I18n';
import styled from 'styled-components';
import UIRouterButton from 'ui-addon-react-router-dom/UIRouterButton';
import UIList from 'UIComponents/list/UIList';
import getLang from 'I18n/utils/getLang';
import { useParams } from 'react-router-dom';
import OverviewModalDescriptionSection from '../../components/TrackLessonOverviewModal/subcomponents/OverviewModalDescriptionSection';
import OverviewModalCurriculumSection from '../../components/TrackLessonOverviewModal/subcomponents/OverviewModalCurriculumSection';
import ReducedLessonCard from '../../components/ContentCards/ReducedContentCard/subcomponents/ReducedLessonCard';
import { device } from '../../constants/StyledComponentConstants';
import UsageTracker from '../../utils/UsageTracker'; //@ts-expect-error Could not find declaration file for module

import OverviewModalCertificateSection from '../../components/TrackLessonOverviewModal/subcomponents/OverviewModalCertificateSection'; //@ts-expect-error Could not find declaration file for module

import OverviewModalProfessorsSection from '../../components/TrackLessonOverviewModal/subcomponents/OverviewModalProfessorsSection';
import UIFlex from 'UIComponents/layout/UIFlex';
import { useAvailableLanguagesByTrack, useOverviewNavigation, useFullTrack } from '../../features/tracks';
import OverviewContent from '../../components/TrackLessonOverviewModal/subcomponents/OverviewContent';
// @ts-expect-error Could not find a declaration file for module
import { useSearchParam } from '../../hooks/searchParamHooks';
import { FILTER_TYPES } from '../../constants/TopFilterConstants';
import { CardMenuIcon } from '../../features/cardMenu';
const ExpandableSection = styled.div.withConfig({
  displayName: "TrackOverviewModalContainer__ExpandableSection",
  componentId: "sc-1a2k9hm-0"
})(["display:flex;align-items:center;.start-lesson-button{margin-left:20px;flex-shrink:0;}@media screen and ", "{flex-direction:column;align-items:flex-start;.start-lesson-button{margin-top:8px;margin-left:0;width:fit-content;}}"], device.mobile);

const TrackOverviewModalContainer = ({
  trackId: trackIdFromProps,
  language: languageFromProps,
  allowFullWidthChildren
}) => {
  const {
    source,
    trackId: trackIdFromParams,
    language: paramLanguage
  } = useParams();
  const trackId = trackIdFromProps && trackIdFromProps.toString() || trackIdFromParams;
  const [[searchParamLanguage]] = useSearchParam(FILTER_TYPES.LANGUAGE);
  const {
    goTo
  } = useOverviewNavigation();
  const {
    trackLanguagesLoading: availableLanguagesLoading,
    trackLanguages: availableLanguagesData
  } = useAvailableLanguagesByTrack(trackId);
  const language = useMemo(() => {
    const chosenLanguage = (languageFromProps || paramLanguage || searchParamLanguage || getLang()).toUpperCase();

    if (availableLanguagesData && availableLanguagesData.length && !availableLanguagesData.includes(chosenLanguage)) {
      // default to English if available
      return !availableLanguagesData.includes('EN') ? 'EN' : availableLanguagesData[0];
    }

    return chosenLanguage;
  }, [availableLanguagesData, languageFromProps, paramLanguage, searchParamLanguage]);
  const {
    trackLoading: loading,
    track,
    trackError: error
  } = useFullTrack(Number(trackId), language);
  const deprecated = track && track.deprecated;
  const deprecationCta = track && track.deprecationCta;
  const videoId = useMemo(() => {
    if (!track) return undefined;

    if (track.introVideo && track.introVideo.vidyardUuid) {
      return track.introVideo.vidyardUuid;
    }

    for (const lesson of track.lessons) {
      const videoContent = lesson.content.find(content => content.vidyardUuid);

      if (videoContent) {
        return videoContent.vidyardUuid;
      }
    }

    return undefined;
  }, [track]);
  const trackAggregatedLessonContents = useMemo(() => {
    return track && track.lessons.reduce((aggregated, lesson) => [...aggregated, ...lesson.content], []);
  }, [track]);
  const professors = useMemo(() => {
    if (!trackAggregatedLessonContents) return [];
    const professorsById = {};
    trackAggregatedLessonContents.forEach(asset => {
      if (asset.professors) {
        asset.professors.forEach(professor => {
          professorsById[professor.id] = professor;
        });
      }
    });
    return Object.values(professorsById);
  }, [trackAggregatedLessonContents]);
  const curriculumLessons = useMemo(() => {
    if (!track || !track.lessons.length) return [];
    return track.lessons.filter(lesson => !lesson.content.every(content => content.contentType === 'LINK'));
  }, [track]);

  const handleChangeSelectedLanguage = ({
    target: {
      value
    }
  }) => {
    UsageTracker.track('interaction', {
      action: 'overview-modal-change-language',
      appearsIn: source,
      trackId: track ? track.id : null,
      name: track ? track.name : null,
      previousLanguage: language,
      newLanguage: value
    });
    goTo({
      id: parseInt(trackId, 10),
      type: 'track',
      language: value
    });
  };

  const handleStartLessonClick = (lesson, index) => () => {
    UsageTracker.track('interaction', {
      action: 'overview-modal-curriculum-click-start',
      contentType: 'LESSON',
      lessonId: lesson.id,
      lessonType: lesson.lessonType[0],
      appearsIn: 'course-overview-curriculum',
      position: index + 1,
      name: lesson.name,
      language
    });
  };

  const handleEnroll = () => {
    UsageTracker.track('interaction', {
      action: 'overview-modal-enroll',
      appearsIn: source,
      contentLanguage: language,
      contentType: 'TRACK',
      lessonId: track ? track.id : null,
      name: track ? track.name : null
    });
  };

  return /*#__PURE__*/_jsxs(OverviewContent, {
    allowFullWidthChildren: allowFullWidthChildren,
    deprecated: !!deprecated,
    deprecationCta: deprecationCta || undefined,
    error: error,
    fallbackThumbnailUrl: track && track.previewThumbnailRectangleUrl,
    headerIcons: /*#__PURE__*/_jsx(CardMenuIcon, {
      language: language,
      trackId: parseInt(trackId, 10),
      use: "on-dark"
    }),
    loading: loading || availableLanguagesLoading,
    videoId: videoId,
    children: [/*#__PURE__*/_jsx(OverviewModalDescriptionSection, {
      availableLanguages: availableLanguagesData,
      availableLanguagesLoading: availableLanguagesLoading,
      contentList: trackAggregatedLessonContents,
      description: track && track.description,
      duration: track && /*#__PURE__*/_jsxs(UIList, {
        inline: true,
        use: "inline-divided",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.overviewModal.numLessons",
          options: {
            number: track.lessons.length
          }
        }), /*#__PURE__*/_jsx(FormattedDuration, {
          from: new Date().getTime() - track.duration * 1000
        })]
      }),
      hasCertificate: track && !!track.certification,
      hasStudyGuide: track && !!track.studyGuide,
      id: track && track.id,
      onChangeSelectedLanguage: handleChangeSelectedLanguage,
      onEnroll: handleEnroll,
      selectedLanguage: language,
      title: track && track.name,
      type: "TRACK"
    }), track && track.certification && /*#__PURE__*/_jsx(OverviewModalCertificateSection, {
      iconUrl: track.icon.location,
      title: track.name
    }), !!professors.length && /*#__PURE__*/_jsx(OverviewModalProfessorsSection, {
      language: language,
      professors: professors
    }), !!curriculumLessons.length && /*#__PURE__*/_jsx(OverviewModalCurriculumSection, {
      children: curriculumLessons.map((lesson, index) => /*#__PURE__*/_jsx(ReducedLessonCard, {
        appearsIn: "course-overview-curriculum",
        expandableContent: /*#__PURE__*/_jsxs(ExpandableSection, {
          className: "p-top-5",
          children: [/*#__PURE__*/_jsx(UIFlex, {
            children: lesson.description
          }), /*#__PURE__*/_jsx(UIRouterButton, {
            className: "start-lesson-button",
            "data-test-id": "start-lesson-button",
            onClick: handleStartLessonClick(lesson, index),
            role: "button",
            size: "small",
            to: `/tracks/${trackId}/${lesson.id}?language=${language}`,
            use: "primary",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.overviewModal.viewLesson"
            })
          })]
        }),
        lessonId: lesson.id,
        position: index + 1,
        showFavoriteTooltip: false,
        use: "on-dark",
        verticalGap: 28
      }, lesson.id))
    })]
  });
};

export default TrackOverviewModalContainer;